import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarRange, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/** Convierte un Date a YYYY-MM-DD sin cambiar zona horaria */
const iso = (d) => d.toISOString().slice(0, 10);
/** Días entre dos fechas (inclusivo) */
const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000) + 1;
/** Suma días a una fecha string YYYY-MM-DD y devuelve string YYYY-MM-DD */
const addDays = (s, n) => {
  const d = new Date(s + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return iso(d);
};

const PRESETS = [
  { key: '7d',  label: '7 días',  days: 7 },
  { key: '14d', label: '14 días', days: 14 },
  { key: '30d', label: '30 días', days: 30 },
  { key: '60d', label: '60 días', days: 60 },
];

export default function SgTimeline() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const hoy = iso(new Date());
  const [desde, setDesde] = useState(hoy);
  const [hasta, setHasta] = useState(addDays(hoy, 13)); // 14 días default
  const [data, setData] = useState({ habitaciones: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/servicios-generales/timeline`, {
        params: { desde, hasta },
        headers,
      });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar timeline');
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, token]);

  useEffect(() => { load(); }, [load]);

  const dias = useMemo(() => {
    const total = Math.max(1, daysBetween(desde, hasta));
    return Array.from({ length: total }, (_, i) => addDays(desde, i));
  }, [desde, hasta]);

  // Agrupar habitaciones por edificio
  const gruposEdificios = useMemo(() => {
    const map = new Map();
    (data.habitaciones || []).forEach(h => {
      const key = (h.edificio || '').trim() || 'Sin edificio';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(h);
    });
    // ordenar habs por número
    for (const arr of map.values()) {
      arr.sort((a, b) => String(a.numero).localeCompare(String(b.numero), 'es', { numeric: true }));
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0], 'es'));
  }, [data]);

  const desplazar = (delta) => {
    setDesde(prev => addDays(prev, delta));
    setHasta(prev => addDays(prev, delta));
  };
  const setPreset = (n) => {
    setDesde(hoy);
    setHasta(addDays(hoy, n - 1));
  };

  return (
    <div className="space-y-4" data-testid="sg-timeline-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Línea de tiempo</h1>
          <p className="text-sm text-slate-500">Ocupación de habitaciones por rango de fechas.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => desplazar(-Math.max(1, daysBetween(desde, hasta) - 1))}
            className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" title="Rango anterior">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
            <CalendarRange className="w-4 h-4 text-emerald-700" />
            <input type="date" value={desde} onChange={e => setDesde(e.target.value)}
              className="text-sm border-0 focus:outline-none w-32" data-testid="timeline-desde" />
            <span className="text-slate-400">→</span>
            <input type="date" value={hasta} min={desde} onChange={e => setHasta(e.target.value)}
              className="text-sm border-0 focus:outline-none w-32" data-testid="timeline-hasta" />
          </div>
          <button onClick={() => desplazar(Math.max(1, daysBetween(desde, hasta) - 1))}
            className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" title="Rango siguiente">
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="inline-flex bg-white border border-slate-200 rounded-lg p-0.5 ml-2">
            {PRESETS.map(p => (
              <button key={p.key} onClick={() => setPreset(p.days)}
                className="px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded"
                data-testid={`preset-${p.key}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-6">Cargando…</div>
      ) : (
        <TimelineGrid dias={dias} gruposEdificios={gruposEdificios} desde={desde} hasta={hasta} />
      )}
    </div>
  );
}

function TimelineGrid({ dias, gruposEdificios, desde, hasta }) {
  const totalHabs = gruposEdificios.reduce((s, [, h]) => s + h.length, 0);
  const totalDias = dias.length;
  const hoyIso = new Date().toISOString().slice(0, 10);
  // Ancho mínimo por día (px) — bajo este umbral se activa scroll horizontal
  const MIN_DIA_PX = 28;
  const minGridPx = totalDias * MIN_DIA_PX;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 bg-slate-50 text-[11px] text-slate-500">
        <div>{totalHabs} habitaciones · {totalDias} días</div>
        <div className="flex items-center gap-3">
          <Legend color="bg-red-500" label="Ocupada" />
          <Legend color="bg-emerald-100 border border-emerald-300" label="Libre" />
          <Legend color="border border-slate-300" label="Fuera del rango" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="flex" style={{ minWidth: 240 + minGridPx }}>
          {/* Columna fija: nombres */}
          <div className="w-[240px] flex-shrink-0 border-r border-slate-200 bg-white">
            <div className="h-10 border-b border-slate-100 px-3 flex items-center text-[10px] font-bold uppercase tracking-wide text-slate-500">Habitación</div>
            {gruposEdificios.map(([nombre, habs]) => (
              <React.Fragment key={nombre}>
                <div className="h-[26px] px-3 flex items-center bg-slate-100 border-b border-slate-200 text-[11px] font-bold text-emerald-900 sticky left-0">
                  {nombre} <span className="text-slate-400 font-normal ml-1">· {habs.length}</span>
                </div>
                {habs.map(h => (
                  <div key={h.id} className="h-[38px] px-3 flex flex-col justify-center border-b border-slate-100 text-xs">
                    <div className="font-bold text-slate-800 leading-tight">{h.numero}</div>
                    <div className="text-[10px] text-slate-400 leading-tight">{h.tipo || '—'}</div>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>

          {/* Grid de días + barras (flex-1: llena el ancho disponible) */}
          <div className="flex-1 relative min-w-0">
            {/* Header días */}
            <div className="h-10 border-b border-slate-100 flex sticky top-0 bg-white z-10">
              {dias.map((d, idx) => {
                const dt = new Date(d + 'T00:00:00');
                const dow = dt.toLocaleDateString('es-CL', { weekday: 'short' }).slice(0, 3);
                const num = String(dt.getDate()).padStart(2, '0');
                const isWeekend = dt.getDay() === 0 || dt.getDay() === 6;
                const isHoy = d === hoyIso;
                return (
                  <div key={idx}
                    className={`flex-1 min-w-0 border-r border-slate-100 text-center text-[10px] py-1 ${isWeekend ? 'bg-slate-50' : ''} ${isHoy ? 'bg-emerald-50 font-bold text-emerald-800' : 'text-slate-500'}`}>
                    <div className="uppercase leading-none">{dow}</div>
                    <div className="text-xs font-bold leading-tight">{num}</div>
                  </div>
                );
              })}
            </div>

            {/* Filas */}
            {gruposEdificios.map(([nombre, habs]) => (
              <React.Fragment key={nombre}>
                <div className="h-[26px] bg-slate-100 border-b border-slate-200" />
                {habs.map(h => {
                  const bloqueada = h.estado === 'deshabilitada' || h.estado === 'mantenimiento' || h.estado === 'aseo';
                  return (
                    <div key={h.id} className="relative h-[38px] border-b border-slate-100 flex">
                      {/* Celdas de cada día como flex-1 */}
                      {dias.map((d, idx) => {
                        const dt = new Date(d + 'T00:00:00');
                        const isWeekend = dt.getDay() === 0 || dt.getDay() === 6;
                        const isHoy = d === hoyIso;
                        // Estado libre por default para cada celda; se pintará rojo si hay asignación arriba
                        const bg = bloqueada
                          ? 'bg-slate-100'
                          : isHoy
                            ? 'bg-emerald-100/60'
                            : isWeekend
                              ? 'bg-emerald-50/50'
                              : 'bg-emerald-50/80';
                        return (
                          <div key={idx}
                            className={`flex-1 min-w-0 border-r border-slate-100 ${bg}`} />
                        );
                      })}
                      {/* Barras de asignaciones absolutas (posicionadas por porcentaje) */}
                      {(h.asignaciones || []).map(a => {
                        const ci = a.check_in;
                        const co = a.check_out || hasta;
                        const startClipped = ci < desde ? desde : ci;
                        const endClipped = co > hasta ? hasta : co;
                        const startIdx = Math.max(0, daysBetween(desde, startClipped) - 1);
                        const spanDays = Math.max(1, daysBetween(startClipped, endClipped));
                        const leftPct = (startIdx / totalDias) * 100;
                        const widthPct = (spanDays / totalDias) * 100;
                        const abiertoIzq = ci < desde;
                        const abiertoDer = !a.check_out || co > hasta;
                        const nombreCorto = (a.nombre_trabajador || 'Ocupante').split(' ').slice(0, 2).join(' ');
                        return (
                          <div key={a.id}
                            title={`${a.nombre_trabajador || ''} · ${a.empresa_nombre || ''}\n${ci}${a.check_out ? ' → ' + a.check_out : ' → abierto'}`}
                            className={`absolute top-1 h-[30px] bg-red-500/90 hover:bg-red-500 text-white text-[10px] font-semibold flex items-center px-1.5 truncate shadow-sm border border-red-700 ${abiertoIzq ? '' : 'rounded-l-md'} ${abiertoDer ? '' : 'rounded-r-md'}`}
                            style={{ left: `calc(${leftPct}% + 1px)`, width: `calc(${widthPct}% - 2px)` }}>
                            <span className="truncate">{nombreCorto}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-block w-3 h-3 rounded-sm ${color}`} />
      <span>{label}</span>
    </div>
  );
}
